export interface Project {
  id: string;
  name: string;
  slug: string;
  faxAccountId: number | null;
  emailDeliveryPaused: boolean;
  emailDeliveryMaxRecipients: number;
  emailDeliveryMaxCampaignRecipients: number;
  emailDeliveryBatchObservationMinutes: number;
  createdAt: string;
}

export type Source = "TRADER" | "TRADER_CONTACT" | "MEMBER" | "FAX_PROSPECT";
export type ContactStatus = "ACTIVE" | "UNSUBSCRIBED" | "BOUNCED";
export type CampaignStatus = "DRAFT" | "SCHEDULED" | "SENDING" | "PAUSED" | "SENT" | "CANCELLED";
export type SendStatus = "QUEUED" | "HELD" | "DISPATCHING" | "SENT" | "BOUNCED" | "COMPLAINED" | "SKIPPED" | "CANCELLED" | "REJECTED" | "UNKNOWN";
export type Channel = "EMAIL" | "SMS" | "LINE" | "FAX";
export type LineMessageType = "TEXT" | "FLEX";
export type EmailTemplateKind = "GENERIC" | "NEWSLETTER" | "FOLLOWUP";
export type EmailTemplateStatus = "DRAFT" | "REVIEW" | "APPROVED" | "ARCHIVED";

export const CHANNEL_LABELS: Record<Channel, string> = {
  EMAIL: "メール",
  SMS: "SMS",
  LINE: "LINE",
  FAX: "FAX DM",
};

export interface SmsTemplate {
  id: string;
  name: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface LineTemplate {
  id: string;
  name: string;
  type: LineMessageType;
  body: string | null;
  altText: string | null;
  flexJson: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface Contact {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  phone: string | null;
  cell: string | null;
  faxNumber: string | null;
  source: Source;
  status: ContactStatus;
  traderId: number | null;
  traderType: string | null;
  traderRank: string | null;
  rfmRank: string | null;
  cpsScore: number | null;
  leadScore: number;
  lastOrderAt: string | null;
  lineUserId: string | null;
  smsConsent: boolean;
  lineFollowing: boolean;
  doNotCall: boolean;
  doNotCallNote: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DoNotCallEntry {
  id: string;
  company: string | null;
  lastName: string | null;
  firstName: string | null;
  phone: string | null;
  doNotCallNote: string | null;
  updatedAt: string;
}

export interface Campaign {
  id: string;
  name: string;
  subject: string;
  htmlBody: string;
  textBody: string | null;
  channel?: Channel;
  faxPdfUrl: string | null;
  faxDocumentId: number | null;
  faxTemplateKey: string | null;
  faxVariantKey: string | null;
  faxSenderName: string | null;
  faxSenderPhone: string | null;
  emailTemplateId: string | null;
  audienceListId: string | null;
  expectedCount: number | null;
  emailBatchEnabled: boolean;
  emailBatchSize: number;
  emailBatchObservationMinutes: number;
  emailBatchHaltedAt: string | null;
  emailBatchHaltReason: string | null;
  deliveryBatches?: Array<{
    id: string;
    ordinal: number;
    status: "HELD" | "RELEASED" | "PASSED" | "HALTED";
    expectedCount: number;
    releasedAt: string | null;
    observeAfter: string | null;
    completedAt: string | null;
    hardBounceCount: number;
    transientBounceCount: number;
    complaintCount: number;
    rejectedCount: number;
    unknownCount: number;
    deliveryDelayCount: number;
    haltReason: string | null;
    _count: { sends: number };
  }>;
  emailTemplate?: {
    id: string;
    name: string;
    subject: string;
    kind: EmailTemplateKind;
    status: EmailTemplateStatus;
    topicKey: string | null;
  } | null;
  audienceList?: {
    id: string;
    name: string;
    mode: AudienceListMode;
    status: AudienceListStatus;
    lastCount: number;
  } | null;
  segmentQuery: SegmentFilter | null;
  status: CampaignStatus;
  scheduledAt: string | null;
  sentAt: string | null;
  totalSent: number;
  totalOpened: number;
  totalClicked: number;
  totalBounced: number;
  createdAt: string;
  updatedAt: string;
  segmentCount?: number | null;
  stats?: {
    byStatus: Record<string, number>;
    opened: number;
    clicked: number;
  };
}

export interface CampaignSend {
  id: string;
  campaignId: string;
  contactId: string;
  status: SendStatus;
  sesMessageId: string | null;
  sentAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface CampaignSendDetail extends CampaignSend {
  contact: {
    email: string;
    company: string | null;
    firstName: string | null;
    lastName: string | null;
  };
}

export interface ContactDetail extends Contact {
  campaignSends: (CampaignSend & {
    campaign: { name: string; subject: string };
  })[];
}

export interface WebEvent {
  id: string;
  contactId: string;
  url: string;
  title: string | null;
  referrer: string | null;
  score: number;
  createdAt: string;
}

export interface SegmentFilter {
  audienceListId?: string;
  contactIds?: string[];
  excludeContactIds?: string[];
  traderIds?: number[];
  traderRank?: string[];
  traderType?: string[];
  source?: string[];
  rfmRank?: string[];
  status?: string[];
  leadScoreMin?: number;
  leadScoreMax?: number;
  lastOrderWithin?: number;
  lastOrderBefore?: number;
  hasFaxNumber?: boolean;
  company?: string[];
  companyContains?: string[];
  companyNotContains?: string[];
}

export type AudienceListTargetType = "CONTACT" | "PROSPECT";
export type AudienceListMode = "DYNAMIC" | "STATIC" | "SNAPSHOT";
export type AudienceListStatus = "DRAFT" | "ACTIVE" | "ARCHIVED";
export type AudienceListMemberStatus = "INCLUDED" | "EXCLUDED";
export type AudienceListMemberSource = "FILTER" | "MANUAL" | "CSV" | "SYSTEM";

export interface AudienceList {
  id: string;
  name: string;
  description: string | null;
  targetType: AudienceListTargetType;
  mode: AudienceListMode;
  status: AudienceListStatus;
  baseFilter: SegmentFilter | ProspectSegmentFilter | null;
  revision: number;
  lastResolvedAt: string | null;
  lastCount: number;
  eligibleCount?: number;
  createdAt: string;
  updatedAt: string;
  _count?: { members: number };
  changes?: AudienceListChange[];
}

export interface AudienceListMember {
  id: string;
  listId: string;
  contactId: string | null;
  prospectId: string | null;
  status: AudienceListMemberStatus;
  source: AudienceListMemberSource;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  contact?: Pick<Contact, "id" | "email" | "company" | "lastName" | "firstName" | "traderType" | "status"> | null;
}

export interface AudienceListChange {
  id: string;
  listId: string;
  action: string;
  payload: Record<string, unknown> | null;
  actorEmail: string | null;
  createdAt: string;
}

export type AudienceType = "CONTACT" | "PROSPECT";
export type ProspectFreshness = "FRESH" | "AGING" | "STALE";
export type ProspectVerificationStatus =
  | "UNVERIFIED"
  | "VERIFIED"
  | "UNREACHABLE"
  | "REFUSED"
  | "INVALID";
export type ProspectIndustryFitValue = "HIGH" | "MEDIUM" | "LOW" | "EXCLUDE" | "UNKNOWN";

export interface ProspectSegmentFilter {
  prospectIds?: string[];
  traderIds?: number[];
  traderType?: string[];
  traderRank?: string[];
  rfmRank?: string[];
  routeCode?: number[];
  salesChannel?: string[];
  listSource?: string[];
  listVersion?: string[];
  status?: string[];
  freshnessStatus?: ProspectFreshness[];
  verificationStatus?: ProspectVerificationStatus[];
  industry?: string[];
  industryFit?: ProspectIndustryFitValue[];
  industryFitScoreMin?: number;
  prefecture?: string[];
  hasPhone?: boolean;
  hasFaxNumber?: boolean;
  hasEmail?: boolean;
  doNotCall?: boolean;
  doNotFax?: boolean;
  chargeSaleFlg?: boolean;
  companySearch?: string;
  sourceUpdatedBefore?: number;
  lastVerifiedBefore?: number;
  hasLastOrder?: boolean;
  lastOrderWithin?: number;
  lastOrderBefore?: number;
  outgoingCallCountMin?: number;
  outgoingCallCountMax?: number;
  lastCallWithin?: number;
  lastCallBefore?: number;
  readyForCall?: boolean;
  readyForFax?: boolean;
  masterOnly?: boolean;
}

export interface Prospect {
  id: string;
  company: string;
  contactName: string | null;
  corporateNumber: string | null;
  phone: string | null;
  cell: string | null;
  faxNumber: string | null;
  email: string | null;
  industry: string | null;
  industryFit: ProspectIndustryFitValue;
  industryFitScore: number;
  industryFitReason: string | null;
  address1: string | null;
  prefecture: string | null;
  city: string | null;
  postalCode: string | null;
  traderId: number | null;
  traderType: string | null;
  traderRank: string | null;
  rfmRank: string | null;
  salesChannel: string | null;
  listSource: string;
  listVersion: string;
  status: string;
  freshnessStatus: ProspectFreshness;
  verificationStatus: ProspectVerificationStatus;
  doNotCall: boolean;
  doNotFax: boolean;
  lastOrderAt: string | null;
  lastCallAt: string | null;
  sourceUpdatedAt: string | null;
  lastVerifiedAt: string | null;
  nextReviewAt: string | null;
  updatedAt: string;
  createdAt: string;
}

export interface ProspectRefreshCandidateSummary {
  listSource: string | null;
  freshnessStatus: ProspectFreshness;
  verificationStatus: ProspectVerificationStatus;
  _count: { _all: number };
}

export interface ProspectRefreshCandidateSample {
  id: string;
  company: string;
  phone: string | null;
  faxNumber: string | null;
  listSource: string;
  listVersion: string;
  freshnessStatus: ProspectFreshness;
  verificationStatus: ProspectVerificationStatus;
  sourceUpdatedAt: string | null;
  lastVerifiedAt: string | null;
}

export interface ProspectRefreshCandidatesResponse {
  staleDays: number;
  summary: ProspectRefreshCandidateSummary[];
  samples: ProspectRefreshCandidateSample[];
  recommendedActions: string[];
}

export interface ProspectQueueItem {
  prospectId: string;
  company: string;
  contactName: string | null;
  phone: string | null;
  traderType: string | null;
  traderRank: string | null;
  rfmRank: string | null;
  routeCode: number | null;
  routeName: string | null;
  salesChannel: string | null;
  industryFit: ProspectIndustryFitValue;
  industryFitScore: number;
  bucket: string;
  bucketLabel: string;
  priorityScore: number;
  rationale: string[];
  freshnessStatus: ProspectFreshness;
  verificationStatus: ProspectVerificationStatus;
  lastOrderAt: string | null;
  lastCallAt: string | null;
  lastVerifiedAt: string | null;
  nextReviewAt: string | null;
  outgoingCallCount: number;
}

export interface ProspectCallQueueResponse {
  generatedAt: string;
  totalCandidates: number;
  eligibleCount: number;
  excludedCount: number;
  excludedByReason: Record<string, number>;
  bucketCounts: Record<string, number>;
  items: ProspectQueueItem[];
}

export interface ProspectExecutableSummary {
  total: number;
  readyForCall: number;
  readyForFax: number;
  masterOnly: number;
  topSources: Array<{
    listSource: string;
    count: number;
  }>;
  recommendations: string[];
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// --- Turn Node types (custom flow editor) ---

export type Intent =
  | "POSITIVE" | "NEGATIVE" | "WRONG" | "BUSY"
  | "NOT_AVAILABLE" | "RECEPTIONIST" | "WHO" | "UNKNOWN" | "SILENCE";

export const ALL_INTENTS: Intent[] = [
  "POSITIVE", "NEGATIVE", "WRONG", "BUSY",
  "NOT_AVAILABLE", "RECEPTIONIST", "WHO", "UNKNOWN", "SILENCE",
];

export const INTENT_LABELS: Record<Intent, string> = {
  POSITIVE: "肯定",
  NEGATIVE: "否定",
  WRONG: "違う人",
  BUSY: "忙しい",
  NOT_AVAILABLE: "不在",
  RECEPTIONIST: "取次",
  WHO: "誰？",
  UNKNOWN: "不明",
  SILENCE: "無言",
};

export const TERMINAL_ACTIONS = [
  "CALLBACK_SCHEDULED",
  "NOT_INTERESTED",
  "WRONG_PERSON",
  "BUSY",
  "EXIT",
  "TRANSFER_REFUSED",
  "OPT_OUT",
  "ANGRY_TERMINATION",
  "NEGATIVE_TERMINATION",
] as const;

export type TerminalAction = typeof TERMINAL_ACTIONS[number];

export const TERMINAL_ACTION_LABELS: Record<TerminalAction, string> = {
  CALLBACK_SCHEDULED: "CB予約",
  NOT_INTERESTED: "興味なし",
  WRONG_PERSON: "別人",
  BUSY: "多忙",
  EXIT: "終了",
  TRANSFER_REFUSED: "取次拒否",
  OPT_OUT: "架電拒否",
  ANGRY_TERMINATION: "強い拒否",
  NEGATIVE_TERMINATION: "否定終了",
};

/** Special non-terminal action: wait for transfer then restart */
export const TRANSFER_WAIT = "TRANSFER_WAIT" as const;

export interface Transition {
  target: string;  // node ID, terminal action, or TRANSFER_WAIT
  speech?: string; // optional speech before transition
}

export interface TurnNode {
  id: string;
  name: string;
  speech: string;
  isEntry?: boolean;
  transitions: Partial<Record<Intent, Transition>>;
}

/** All valid transition targets: other node IDs + terminal actions + TRANSFER_WAIT */
export function getTransitionTargets(nodes: TurnNode[]): { value: string; label: string }[] {
  const targets: { value: string; label: string }[] = [];
  for (const n of nodes) {
    targets.push({ value: n.id, label: `${n.name} (${n.id})` });
  }
  for (const a of TERMINAL_ACTIONS) {
    targets.push({ value: a, label: TERMINAL_ACTION_LABELS[a] });
  }
  targets.push({ value: TRANSFER_WAIT, label: "転送待ち" });
  return targets;
}

export const DEFAULT_TURNS_TEMPLATE: TurnNode[] = [
  {
    id: "greeting",
    name: "挨拶",
    speech: "お世話になります。{company}です。自動車のヘッドライトまわりのご担当者様はいらっしゃいますか？",
    isEntry: true,
    transitions: {
      POSITIVE: { target: "interest", speech: "恐れ入ります。" },
      NEGATIVE: { target: "WRONG_PERSON", speech: "失礼いたしました。" },
      WRONG: { target: "transfer_ask", speech: "ご担当者様におつなぎいただけますか？" },
      NOT_AVAILABLE: { target: "TRANSFER_REFUSED", speech: "承知いたしました。改めてお電話いたします。" },
      BUSY: { target: "BUSY", speech: "お忙しいところ失礼いたしました。" },
      WHO: { target: "greeting", speech: "{company}です。{product_pitch}の件で、ご担当者様はいらっしゃいますか？" },
    },
  },
  {
    id: "interest",
    name: "困りごと確認",
    speech: "最近、純正HID車のロービーム車検で、光量不足に当たる案件って増えていませんか？",
    transitions: {
      POSITIVE: { target: "callback", speech: "そうでしたか。" },
      NEGATIVE: { target: "materials", speech: "承知しました。" },
      WRONG: { target: "WRONG_PERSON", speech: "大変失礼いたしました。" },
      BUSY: { target: "BUSY", speech: "お忙しいところ失礼いたしました。" },
      WHO: { target: "interest", speech: "{product_pitch}の件です。最近、純正HID車のロービーム車検でお困りの案件はありますか？" },
    },
  },
  {
    id: "materials",
    name: "資料案内",
    speech: "もし得意先様から相談が来た時用に、{product_name}の資料を1枚お送りしてもよろしいですか？",
    transitions: {
      POSITIVE: { target: "CALLBACK_SCHEDULED", speech: "承知しました。資料をお送りして、担当にも共有のうえ、改めてご連絡いたします。" },
      NEGATIVE: { target: "NOT_INTERESTED", speech: "承知いたしました。" },
      WRONG: { target: "WRONG_PERSON", speech: "大変失礼いたしました。" },
      BUSY: { target: "BUSY", speech: "お忙しいところ失礼いたしました。" },
      WHO: { target: "materials", speech: "{product_pitch}の資料案内です。1枚だけお送りしてもよろしいですか？" },
    },
  },
  {
    id: "callback",
    name: "コールバック",
    speech: "ロービーム車検対策で使われているLEDがあるのですが、ご案内してもよろしいですか？",
    transitions: {
      POSITIVE: { target: "CALLBACK_SCHEDULED", speech: "ありがとうございます。担当から短くご案内いたします。ご都合が悪ければその場で調整いたします。" },
      BUSY: { target: "CALLBACK_SCHEDULED", speech: "承知しました。では、担当にも共有のうえ、改めてご連絡いたします。お忙しいところありがとうございました。" },
      NEGATIVE: { target: "NOT_INTERESTED", speech: "承知いたしました。" },
    },
  },
  {
    id: "transfer_ask",
    name: "取次依頼",
    speech: "ご担当者様におつなぎいただけますか？",
    transitions: {
      POSITIVE: { target: "TRANSFER_WAIT", speech: "恐れ入ります。お願いいたします。" },
      RECEPTIONIST: { target: "TRANSFER_WAIT", speech: "恐れ入ります。お願いいたします。" },
      NEGATIVE: { target: "TRANSFER_REFUSED", speech: "承知いたしました。改めてお電話いたします。" },
      NOT_AVAILABLE: { target: "TRANSFER_REFUSED", speech: "承知いたしました。改めてお電話いたします。" },
      WRONG: { target: "WRONG_PERSON", speech: "大変失礼いたしました。" },
      BUSY: { target: "BUSY", speech: "お忙しいところ失礼いたしました。" },
    },
  },
];

// --- Call Campaigns ---

export type CallCampaignStatus = "DRAFT" | "SCHEDULED" | "RUNNING" | "PAUSED" | "COMPLETED" | "CANCELLED";
export type CallOutcome =
  | "PENDING" | "CALLING" | "CALLBACK_SCHEDULED" | "NOT_INTERESTED"
  | "WRONG_PERSON" | "NO_ANSWER" | "BUSY" | "VOICEMAIL" | "BOT_DETECTED"
  | "TRANSFER_TIMEOUT" | "TRANSFER_REFUSED" | "OPT_OUT"
  | "ANGRY_TERMINATION" | "NEGATIVE_TERMINATION" | "FAILED" | "CANCELLED";

export interface CallScript {
  id: string;
  name: string;
  greeting: string;
  productName: string;
  productPitch: string;
  callbackMessage: string;
  farewellMessage: string;
  companyName: string;
  turns: unknown;
  ttsProvider: "openai" | "google";
  ttsModel: string;
  ttsVoice: string;
  ttsInstructions: string;
  createdAt: string;
  updatedAt: string;
}

export interface CallCampaign {
  id: string;
  name: string;
  audienceType: AudienceType;
  segmentQuery: SegmentFilter | ProspectSegmentFilter | null;
  status: CallCampaignStatus;
  callScriptId: string;
  emailCampaignId: string | null;
  triggerRule: Record<string, unknown> | null;
  totalCalls: number;
  totalAnswered: number;
  totalCallbacks: number;
  totalFailed: number;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  callScript?: { name: string; productName: string };
  emailCampaign?: { id: string; name: string } | null;
  resultStats?: Record<string, number>;
}

export interface ChannelExportImportSummary {
  schemaVersion: "channel-export-v1";
  listId: string;
  exportId: string;
  title: string;
  listSource: string;
  listVersion: string;
  totalRows: number;
  callableRows: number;
  skippedRows: number;
  blockedRows: number;
}

export interface ChannelExportImportResult extends ChannelExportImportSummary {
  ok: boolean;
  dryRun: boolean;
  created: number;
  updated: number;
  campaignId: string | null;
}

export interface CallCampaignLiveCall {
  queueId: number;
  campaignId: string;
  company: string;
  name: string;
  phone: string;
  attempt: number;
  updatedAt: string;
}

export interface CallCampaignLiveStatus {
  available: boolean;
  campaignId: string;
  status: CallCampaignStatus;
  pending: number;
  calling: number;
  paused: number;
  completed: number;
  failed: number;
  cancelled: number;
  total: number;
  activeCalls: CallCampaignLiveCall[];
  queue: Record<string, number>;
  refreshedAt: string;
}

export interface CallRecordingPlayback {
  url: string;
  recordingKey: string;
}

export type CallAudioReviewStatus = "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED" | "SKIPPED";

export interface CallTrace {
  callConnectedAt?: number | null;
  amdStartedAt?: number | null;
  amdResolvedAt?: number | null;
  amdResult?: string | null;
  postAmdQuietStartedAt?: number | null;
  postAmdQuietSatisfiedAt?: number | null;
  postAmdQuietObservedActiveAudio?: boolean | null;
  postAmdQuietTimedOut?: boolean | null;
  greetingStartAt?: number | null;
  greetingEndAt?: number | null;
  firstCustomerAudioAt?: number | null;
  firstCustomerSpeechAt?: number | null;
  firstFinalTranscriptAt?: number | null;
  firstCustomerRawText?: string | null;
  firstCustomerResolvedText?: string | null;
  firstCustomerRecognitionMode?: string | null;
  firstCustomerSpeechFrames?: number | null;
  firstAssistantResponseAt?: number | null;
  callEndedAt?: number | null;
}

export interface CallAudioReview {
  id: string;
  callResultId: string;
  status: CallAudioReviewStatus;
  score: number | null;
  flags: string[] | null;
  summary: string | null;
  initialSilenceMs: number | null;
  greetingLeadMs: number | null;
  overlapMs: number | null;
  customerTalkMs: number | null;
  assistantTalkMs: number | null;
  silenceMs: number | null;
  transferLikeAudio: boolean | null;
  reviewerVersion: string | null;
  rawMetrics: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface CallResult {
  id: string;
  callCampaignId: string;
  contactId: string;
  phone: string;
  outcome: CallOutcome;
  attempt: number;
  turns: number;
  duration: number;
  transcript: Array<{ turn: number; role: string; text: string }> | null;
  recordingKey: string | null;
  trace: CallTrace | null;
  audioReviewedAt: string | null;
  aicallerQueueId: number | null;
  aicallerCallId: string | null;
  createdAt: string;
  updatedAt: string;
  audioReview?: CallAudioReview | null;
  contact?: {
    email: string;
    company: string | null;
    lastName: string | null;
    firstName: string | null;
  };
}

// --- Sequences ---

export type SequenceStatus = "ACTIVE" | "PAUSED" | "ARCHIVED";
export type SequenceTriggerType =
  | "MANUAL" | "ON_CAMPAIGN_OPEN" | "ON_CAMPAIGN_CLICK"
  | "ON_WEB_EVENT" | "ON_SCORE_THRESHOLD" | "ON_INACTIVITY";
export type EnrollmentStatus = "ACTIVE" | "PAUSED" | "COMPLETED" | "EXITED";

export interface SequenceStep {
  id: string;
  sequenceId: string;
  stepNumber: number;
  delayDays: number;
  channel: Channel;
  emailTemplateId: string | null;
  smsTemplateId: string | null;
  lineTemplateId: string | null;
  faxDocumentId: number | null;
  subject: string | null;
  condition: { branch: "opened" | "clicked" | "none"; ifTrue?: number; ifFalse?: number } | null;
  emailTemplate?: { id: string; name: string; subject: string };
  smsTemplate?: { id: string; name: string };
  lineTemplate?: { id: string; name: string; type: LineMessageType };
  createdAt: string;
  updatedAt: string;
}

export interface Sequence {
  id: string;
  name: string;
  status: SequenceStatus;
  triggerType: SequenceTriggerType;
  triggerConfig: Record<string, unknown> | null;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  steps?: SequenceStep[];
  _count?: { steps: number; enrollments: number };
  enrollmentStats?: Record<string, number>;
}

export interface SequenceEnrollment {
  id: string;
  sequenceId: string;
  contactId: string;
  currentStep: number;
  status: EnrollmentStatus;
  nextStepAt: string;
  lastSendId: string | null;
  exitReason: string | null;
  createdAt: string;
  updatedAt: string;
  contact?: {
    email: string;
    company: string | null;
    lastName: string | null;
    firstName: string | null;
    status: ContactStatus;
  };
}

export interface ConversionEvent {
  id: string;
  contactId: string;
  campaignId: string | null;
  orderId: number;
  orderAmount: number;
  orderDate: string;
  attributionType: string;
  createdAt: string;
}

export const SEQUENCE_TRIGGER_LABELS: Record<SequenceTriggerType, string> = {
  MANUAL: "手動",
  ON_CAMPAIGN_OPEN: "キャンペーン開封",
  ON_CAMPAIGN_CLICK: "キャンペーンクリック",
  ON_WEB_EVENT: "Web閲覧",
  ON_SCORE_THRESHOLD: "スコア閾値",
  ON_INACTIVITY: "非活動",
};

export const SEQUENCE_STATUS_LABELS: Record<SequenceStatus, string> = {
  ACTIVE: "有効",
  PAUSED: "停止中",
  ARCHIVED: "アーカイブ",
};

export const ENROLLMENT_STATUS_LABELS: Record<EnrollmentStatus, string> = {
  ACTIVE: "進行中",
  PAUSED: "一時停止",
  COMPLETED: "完了",
  EXITED: "退出",
};

// --- FAX Received ---

export interface FaxReceived {
  id: string;
  transmissionId: number;
  senderNumber: string;
  contactId: string | null;
  receivedAt: string;
  status: string;
  pageCount: number | null;
  durationSec: number | null;
  documentId: number | null;
  ocrStatus: string | null;
  ocrSummary: string | null;
  faxType: string | null;
  projectId: string;
  createdAt: string;
  contact?: {
    id: string;
    email: string;
    company: string | null;
    lastName: string | null;
    firstName: string | null;
  };
}

// --- Meta Ads ---

export type MetaAudienceSyncStatus = "ACTIVE" | "PAUSED" | "ERROR";
export type MetaEventStatus = "PENDING" | "SENT" | "FAILED";

export interface MetaAudience {
  id: string;
  name: string;
  metaAudienceId: string | null;
  adAccountId: string;
  segmentQuery: SegmentFilter | null;
  syncStatus: MetaAudienceSyncStatus;
  lastSyncedAt: string | null;
  lastSyncCount: number;
  lastSyncError: string | null;
  createdAt: string;
  updatedAt: string;
  syncLogs?: MetaAudienceSyncLog[];
}

export interface MetaAudienceSyncLog {
  id: string;
  audienceId: string;
  added: number;
  removed: number;
  totalMatched: number;
  error: string | null;
  createdAt: string;
}

export interface MetaConversionLog {
  id: string;
  conversionEventId: string | null;
  contactId: string | null;
  eventName: string;
  eventId: string;
  status: MetaEventStatus;
  errorMessage: string | null;
  pixelId: string;
  createdAt: string;
}

export interface MetaSettings {
  configured: boolean;
  capiConfigured: boolean;
  adAccountId: string | null;
  pixelId: string | null;
  testEventCode: string | null;
}

export const META_SYNC_STATUS_LABELS: Record<MetaAudienceSyncStatus, string> = {
  ACTIVE: "有効",
  PAUSED: "停止中",
  ERROR: "エラー",
};

export const META_EVENT_STATUS_LABELS: Record<MetaEventStatus, string> = {
  PENDING: "待機中",
  SENT: "送信済み",
  FAILED: "失敗",
};

// --- Timeline ---

export interface LineMessage {
  id: string;
  contactId: string;
  direction: "INCOMING" | "OUTGOING";
  messageType: string;
  body: string | null;
  lineMessageId: string | null;
  projectId: string;
  createdAt: string;
}

export type TimelineItemType = "CAMPAIGN_SEND" | "ENGAGEMENT" | "CALL" | "FAX_RECEIVED" | "WEB_EVENT" | "SEQUENCE" | "LINE_MESSAGE";
export type TimelineChannel = "EMAIL" | "SMS" | "LINE" | "FAX" | "CALL" | "WEB" | "SEQUENCE";

export interface TimelineItem {
  id: string;
  type: TimelineItemType;
  channel: TimelineChannel;
  timestamp: string;
  summary: string;
  status?: string;
  metadata: Record<string, any>;
}

export interface TimelineResponse {
  data: TimelineItem[];
  nextCursor: string | null;
  hasMore: boolean;
}
